import { PageBreadcrumbs } from '../../components/dugout/PageIntro'
import React from 'react'
import { Link } from 'react-router-dom'
import { DugoutNav } from '@/components/dugout/DugoutNav'
import { DugoutFooter } from '@/components/dugout/DugoutFooter'
import {
  Linkedin,
  Mail
} from 'lucide-react'
import '../../theme/dugout-redesign.css'

// Professional experience data - from JayPatel_PM_CV_2026.md
const experience = [
  {
    company: 'RWND',
    role: 'Product Builder / Writer',
    period: 'Oct 2024 – Present',
    highlights: [
      'Creating an ecosystem of products that celebrate human creativity, memory, and community.',
      'Building tools and platforms that help humans cultivate their own creativity rather than outsource it to machines.'
    ],
  },
  {
    company: 'Adobe (Photoshop)',
    role: 'Sr. Product Manager – Photoshop Growth',
    period: 'July 2024 – Dec 2024',
    highlights: [
      'Launched personalized user experiences and workspace optimizations to improve onboarding and feature discovery flows.',
      'Led rapid experimentation strategy across growth initiatives, partnering with design, engineering and data science teams.'
    ],
  },
  {
    company: 'GoLinks',
    role: 'Product Consultant – GoLinks, GoSearch, GoProfiles',
    period: 'Dec 2023 – Mar 2024',
    highlights: [
      'Launched critical customer-requested features such as QR codes, Geo-based links, and a Multi-link launcher to improve user retention and reduce churn.',
      'Integrated generative AI capabilities of GoSearch to demonstrate the comprehensive value of the GoLinks product suite.',
      'Advised on strategic growth plans, focusing on product expansion and updated plans and pricing to attract new market segments.'
    ],
  },
  {
    company: 'DSCVR',
    role: 'Head of Product – Web3 Community Platform',
    period: '2023',
    highlights: [
      'Led design and product teams through research and development to find product-market fit for a decentralized, community-driven web3 social space built on blockchain technology. Increased DAU 10X and reached 200K+ users.',
      'Aligned team on 2023 roadmap to drive growth and engagement, platform upgrades, and partner integrations to enable a multi-chain strategy for Ethereum, Solana, Polygon, and Internet Computer.',
      'Defined product strategy, agile processes, and cross-team operations to accelerate product development cycles.'
    ],
  },
  {
    company: 'Candy Digital (DC Comics)',
    role: 'Lead Product Manager – Digital Collectibles Marketplace',
    period: '2022',
    highlights: [
      'Managed product team to develop the DC Marketplace (nft.dcuniverse.com) with a focus on NFT drops, crypto and fiat purchase flows, and core marketplace features to launch DC Comics\' first digital collectibles marketplace.',
      'Led cross-functional teams across blockchain engineering, creative studio, and product to define the NFT strategy, utility roadmap, and community experience with DC Comics.',
      'Coordinated the generative 3D NFT production process for The Bat Cowl Collection. Sold 11K NFTs at launch.'
    ],
  },
  {
    company: 'Sendoso',
    role: 'Lead Product Manager – Gifting Experience',
    period: '2021',
    highlights: [
      'Shipped new gifting flow with brand and team management features to drive account expansion within large organizations and agencies.',
      'Collaborated on customer research, UX workshops, and user interviews to refine sales & marketing customer journeys.'
    ],
  },
  {
    company: 'Litmus Software',
    role: 'Principal Product Manager – Email Analytics & Insights',
    period: '2018 – 2021',
    highlights: [
      'Built the business case and roadmap for evolving the Litmus Email Analytics product. Applied machine learning to email engagement data and content to drive R&D for future AI initiatives.',
      'Championed effort to build a more consistent product design system for Litmus to overhaul the IA and navigation.',
      'Assembled a product-led growth (PLG) swat team to redesign the Litmus signup flow and onboarding experience.'
    ],
  },
  {
    company: 'Creative Market',
    role: 'Sr. Product Manager – Marketplace Growth',
    period: '2018',
    highlights: [
      'Established a product growth team to focus on maximizing gross market value (GMV) and other key performance metrics through improved search & discovery, shopping cart and checkout experience.'
    ],
  },
  {
    company: 'Atlassian',
    role: 'Sr. Product Manager – Buyer Experience & PLG',
    period: '2015 – 2018',
    highlights: [
      'Managed team to drive the strategy and roadmap for buyer experience, try and buy funnel optimization, and product led growth for Jira, Confluence, Bitbucket, and Trello.',
      'Led to 20%+ increase in multi-product sign up and second product activation, fueling growth from $300M in revenue to over $1B through IPO.',
      'Guided content strategy and defined end to end user journeys for demand gen and marketing campaigns.'
    ],
  },
  {
    company: 'Hightail',
    role: 'Sr. Product Manager – Team Spaces',
    period: '2014 – 2015',
    highlights: [
      'Conducted customer development research and evaluated the market to help define the product vision for Team Spaces, a collaboration app for creative professionals.',
      'Led design and development for Spaces across web, mobile, and desktop platforms.'
    ],
  },
  {
    company: 'Box',
    role: 'Sr. Product Marketing Manager – Freemium Growth',
    period: '2012 – 2014',
    highlights: [
      'Worked with product management to improve user activation and DAU through UX research and A/B testing.',
      'Enhanced user journey and managed end-user communications for over 20M+ free users of Box.'
    ],
  },
  {
    company: 'TiVo',
    role: 'Product Manager – Apps & APIs',
    period: '2012',
    highlights: [
      'Defined strategy and roadmap for new TiVo Client SDK and Web APIs.',
      'Led business development with 3rd party apps including YouTube, Spotify, MLB.TV, and others.'
    ],
  },
  {
    company: 'Mozilla (Firefox)',
    role: 'Product Manager / Product Marketing / Engineering',
    period: '2004 – 2011',
    highlights: [
      'Revamped developer engagement by creating Mozilla Developer Network (MDN) at developer.mozilla.org.',
      'Created Mozilla Campus Reps program, growing from 50 students to a global community of over 3,000 Reps.',
      'Managed security and stability releases of Firefox/Thunderbird 1.0.x and 2.0.x.'
    ],
  },
]

// Skills organized by category
const skills = {
  'Product Management': ['Product Strategy', 'Roadmap Planning', 'User Research', 'A/B Testing', 'Data Analytics', 'Agile/Scrum'],
  'Growth': ['Product-Led Growth', 'Experimentation', 'Conversion Optimization', 'User Onboarding', 'Retention Strategy'],
  'Technical': ['SQL', 'APIs', 'Web3/Blockchain', 'AI/ML Integration', 'Developer Tools'],
  'Leadership': ['Cross-functional Leadership', 'Team Building', 'Stakeholder Management', 'Executive Communication']
}

// Education
const education = [
  {
    degree: 'MBA, Marketing & Entrepreneurship',
    school: 'Haas School of Business, University of California, Berkeley'
  },
  {
    degree: 'BS, Computer Science',
    school: 'Jacobs School of Engineering, University of California, San Diego',
    minor: 'Minor in Cognitive Science & HCI'
  }
]

const JayPatelResume = () => {
  return (
    <div data-section="about" className="dugout-redesign dugout-resume min-h-screen bg-[var(--dug-bg-deep)] text-[var(--dug-text-primary)]">
      <DugoutNav />

      <main className="pb-16"><div className="ds-container page-context"><PageBreadcrumbs items={[{ label: 'Home', href: '/' }, { label: 'Work', href: '/portfolio' }, { label: 'Résumé' }]} /></div>
        <div className="max-w-[850px] mx-auto px-6">

          {/* Resume Document */}
          <div className="bg-[var(--dug-bg-surface)] border border-[var(--dug-border)] rounded-lg p-8 md:p-12">

            {/* Header */}
            <header className="text-center border-b border-[var(--dug-border)] pb-8 mb-8">
              <h1 className="mb-4 ds-type-hero">
                Jay Patel
              </h1>
              <div className="flex flex-wrap justify-center gap-x-6 gap-y-2 text-sm text-[var(--dug-text-muted)]">
                <a
                  href="mailto:jaypatel@gmail.com"
                  className="flex items-center gap-1.5 hover:text-[var(--dug-coral)] transition-colors"
                >
                  <Mail className="w-4 h-4" />
                  jaypatel@gmail.com
                </a>
                <a
                  href="https://linkedin.com/in/jay-patel-pm"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-1.5 hover:text-[var(--dug-coral)] transition-colors"
                >
                  <Linkedin className="w-4 h-4" />
                  linkedin.com/in/jay-patel-pm
                </a>
              </div>
            </header>

            {/* Summary */}
            <section className="mb-10">
              <p className="text-[var(--dug-text-secondary)] leading-relaxed ds-type-body">
                Product leader who has built digital experiences across web browsers, open source software, consumer apps, B2B platforms, marketplaces and Web3. I love crafting product strategy through customer insights and data, while pushing for exceptional design and user experience. I've made my biggest impact fostering community, championing growth, and leading cross-functional teams to ship innovative products users love.
              </p>
            </section>

            {/* Experience */}
            <section className="mb-10">
              <h2 className="uppercase  text-[var(--dug-coral)] mb-6 ds-type-section">
                Experience
              </h2>

              <div className="space-y-6">
                {experience.map((job, idx) => (
                  <div key={idx} className="relative">
                    {/* Job Header */}
                    <div className="flex flex-col sm:flex-row sm:items-baseline sm:justify-between gap-1 mb-2">
                      <div>
                        <h3 className="text-[var(--dug-text-primary)] ds-type-card">
                          {job.company}
                        </h3>
                        <p className="text-[var(--dug-text-secondary)] ds-type-body">
                          {job.role}
                        </p>
                      </div>
                      <div className="text-sm text-[var(--dug-text-muted)] sm:text-right whitespace-nowrap">
                        {job.period}
                      </div>
                    </div>

                    {/* Highlights */}
                    <ul className="space-y-1.5 text-sm text-[var(--dug-text-secondary)]">
                      {job.highlights.map((highlight, hIdx) => (
                        <li key={hIdx} className="flex gap-2 leading-relaxed">
                          <span className="text-[var(--dug-text-muted)] select-none">•</span>
                          <span>{highlight}</span>
                        </li>
                      ))}
                    </ul>

                    {/* Divider (except for last item) */}
                    {idx < experience.length - 1 && (
                      <div className="border-b border-[var(--dug-border)] border-dashed mt-6" />
                    )}
                  </div>
                ))}
              </div>
            </section>

            {/* Education */}
            <section className="mb-10">
              <h2 className="uppercase  text-[var(--dug-coral)] mb-4 ds-type-section">
                Education
              </h2>
              <div className="space-y-3">
                {education.map((edu, idx) => (
                  <div key={idx}>
                    <h3 className="text-[var(--dug-text-primary)] ds-type-card">
                      {edu.degree}
                    </h3>
                    <p className="text-[var(--dug-text-secondary)] ds-type-body">
                      {edu.school}
                    </p>
                    {edu.minor && (
                      <p className="text-[var(--dug-text-muted)] ds-type-body">
                        {edu.minor}
                      </p>
                    )}
                  </div>
                ))}
              </div>
            </section>

            {/* Skills */}
            <section>
              <h2 className="uppercase  text-[var(--dug-coral)] mb-4 ds-type-section">
                Skills
              </h2>

              <div className="space-y-3">
                {Object.entries(skills).map(([category, items]) => (
                  <div key={category} className="flex flex-col sm:flex-row sm:gap-3">
                    <span className="font-medium text-[var(--dug-text-primary)] sm:min-w-[160px] text-sm">
                      {category}:
                    </span>
                    <span className="text-sm text-[var(--dug-text-secondary)]">
                      {items.join(' · ')}
                    </span>
                  </div>
                ))}
              </div>
            </section>

          </div>

          {/* Footer Actions */}
          <div className="flex flex-wrap gap-4 justify-center mt-8">
            <Link
              to="/#contact"
              className="dug-btn dug-btn-primary"
            >
              Get in Touch
            </Link>
            <Link
              to="/portfolio"
              className="dug-btn dug-btn-secondary"
            >
              View Portfolio
            </Link>
          </div>

        </div>
      </main>

      <DugoutFooter />
    </div>
  )
}

export default JayPatelResume
